import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CircularProgress, Container, Typography } from '@mui/material';
import theme from '../../../theme/theme';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

import { useGetEmployeeById } from '../hooks/useGetEmployeeById';
import type { DataGroupKey } from '../config/dataGroups';
import { useDataGroupAccess } from '../config/dataGroups';
import {
  EMPLOYEE_SECTIONS,
  IDENTITY_DOCUMENTS_GROUP,
  DEPENDENTS_GROUP,
  TABS,
  TAB_PERMISSION,
  visibleTabIndexes,
} from '../config/employeeDetailSections';
import { usePermissions } from '../../permissions/usePermissions';
import {
  PAGE_BG,
  SECTION_VISUAL,
  SECTION_ICON,
  type SectionKey,
} from '../config/employeeDetailTokens';
import { SubHeader } from '../components/detail/SubHeader';
import { NavTabs } from '../components/detail/NavTabs';
import { ColumnSections } from '../../../components/ui/sectionCard/SectionCard';
import { DependentsTable } from '../components/detail/DependentsTable';
import { DocumentsCard } from '../components/detail/DocumentsCard';
import { EmergencyContactsCard } from '../components/detail/EmergencyContactsCard';
import { PaymentMethodsCard } from '../components/detail/PaymentMethodsCard';
import { PaymentSections } from '../../payments/components/PaymentSections';
import { EmployeeContracts } from '../../contracts/components/EmployeeContracts';
import type { EmployeeRow } from '../employeeTypes';

// ── Page ────────────────────────────────────────────────────────────────────────

export function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { can } = usePermissions();

  // Access to the record (HOMCO-640). `employee_record` gates the record as a
  // whole; without it there are no tabs and a single inline message explains it
  // (no /403 redirect — the message stays in context). With it, each tab obeys its
  // own `employee_record.*` line. Permissions have settled by the time this page
  // mounts, so the first visible tab is a safe initial selection.
  const canViewRecord = can('employee_record', 'display');
  // HOMCO-588: no `GET /employees/:id` without record access (the server answers 403).
  const { data: employee, loading, error } = useGetEmployeeById(id ?? '', canViewRecord);
  const visibleIndexes = canViewRecord ? visibleTabIndexes(can) : [];
  const [selectedTab, setSelectedTab] = useState(() => visibleIndexes[0] ?? -1);
  // Permissions can change after mount (profile refreshed): a selected tab that is no longer
  // visible falls back to the first visible one, while a still-visible selection is kept.
  const activeTab = visibleIndexes.includes(selectedTab) ? selectedTab : (visibleIndexes[0] ?? -1);
  const activeTabAllowed = activeTab >= 0 && can(TAB_PERMISSION[TABS[activeTab]], 'display');

  const gdprAccess = useDataGroupAccess('gdpr');
  const contractAccess = useDataGroupAccess(IDENTITY_DOCUMENTS_GROUP);
  const classified2Access = useDataGroupAccess(DEPENDENTS_GROUP);

  const groupAccess: Record<DataGroupKey, boolean> = {
    gdpr: gdprAccess.canRead,
    contract: contractAccess.canRead,
    classified_2: classified2Access.canRead,
  };

  // Evaluated before `loading` / `error` / `!employee`: with the fetch skipped, `employee` and
  // `error` are both null and the page would otherwise render blank (HOMCO-588).
  if (!canViewRecord) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/employees')}
          sx={{ mb: 2, textTransform: 'none' }}
        >
          {t('employeeDetail.backToList')}
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
          <Typography variant="body1" color="text.secondary">
            {t('employeeDetail.noRecordAccess')}
          </Typography>
        </Box>
      </Box>
    );
  }

  // `loading` only turns true once the effect has fired the request (and stays false while the
  // fetch is skipped): `!employee && !error` covers the frame before it, and the false → true
  // flip of `canViewRecord`, so the page never renders blank.
  if (loading || (!employee && !error)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/employees')}
          sx={{ mb: 2, textTransform: 'none' }}
        >
          {t('employeeDetail.backToList')}
        </Button>
        <Alert severity="error">{t('employeeDetail.loadError')}</Alert>
      </Box>
    );
  }

  if (!employee) return null;

  // Split column 2: contact+emergency render side-by-side; payment card inserted before comment
  const col1 = EMPLOYEE_SECTIONS.filter(
    s => s.column === 1 && groupAccess[s.group as DataGroupKey],
  );
  const col2Main = EMPLOYEE_SECTIONS.filter(
    s =>
      s.column === 2 &&
      s.id !== 'comment' &&
      s.id !== 'contact' &&
      s.id !== 'death' &&
      s.id !== 'pins' &&
      groupAccess[s.group as DataGroupKey],
  );
  // Column 2 order after the Coordonnées / Contact d'urgence pair:
  // Paiement → PINs → Décès → Commentaire.
  const col2Death = EMPLOYEE_SECTIONS.filter(
    s => s.id === 'death' && groupAccess[s.group as DataGroupKey],
  );
  const col2Pins = EMPLOYEE_SECTIONS.filter(
    s => s.id === 'pins' && groupAccess[s.group as DataGroupKey],
  );
  const col2Contact = EMPLOYEE_SECTIONS.filter(
    s => s.id === 'contact' && groupAccess[s.group as DataGroupKey],
  );
  const col2Trail = EMPLOYEE_SECTIONS.filter(
    s => s.column === 2 && s.id === 'comment' && groupAccess[s.group as DataGroupKey],
  );

  // Identity cards, built once and then placed into the 2-column grid below.
  const col1Cards = (
    <ColumnSections<EmployeeRow, SectionKey>
      sections={col1}
      data={employee}
      t={t}
      visualMap={SECTION_VISUAL}
      iconMap={SECTION_ICON}
      collapsible
    />
  );
  const documentsCard = contractAccess.canRead ? (
    <DocumentsCard
      documents={employee.identity_documents ?? []}
      icon={SECTION_ICON.documents(SECTION_VISUAL.documents.iconColor)}
      iconBg={SECTION_VISUAL.documents.iconBg}
      title={t('employeeDetail.sections.documents')}
      t={t}
      collapsible
      defaultExpanded={false}
    />
  ) : null;
  const addressCard = (
    <ColumnSections<EmployeeRow, SectionKey>
      sections={col2Main}
      data={employee}
      t={t}
      visualMap={SECTION_VISUAL}
      iconMap={SECTION_ICON}
      collapsible
    />
  );
  const contactCard =
    col2Contact.length > 0 ? (
      <ColumnSections<EmployeeRow, SectionKey>
        sections={col2Contact}
        data={employee}
        t={t}
        visualMap={SECTION_VISUAL}
        iconMap={SECTION_ICON}
        collapsible
      />
    ) : null;
  const emergencyCard = classified2Access.canRead ? (
    <EmergencyContactsCard
      contacts={employee.emergency_contacts ?? []}
      icon={SECTION_ICON.emergency(SECTION_VISUAL.emergency.iconColor)}
      iconBg={SECTION_VISUAL.emergency.iconBg}
      title={t('employeeDetail.sections.emergency')}
      t={t}
      stack
      collapsible
      defaultExpanded={false}
    />
  ) : null;
  const paymentCard = gdprAccess.canRead ? (
    <PaymentMethodsCard employee={employee} collapsible defaultExpanded={false} />
  ) : null;
  const commentCard =
    col2Trail.length > 0 ? (
      <ColumnSections<EmployeeRow, SectionKey>
        sections={col2Trail}
        data={employee}
        t={t}
        visualMap={SECTION_VISUAL}
        iconMap={SECTION_ICON}
        collapsible
      />
    ) : null;
  const deathCard =
    col2Death.length > 0 ? (
      <ColumnSections<EmployeeRow, SectionKey>
        sections={col2Death}
        data={employee}
        t={t}
        visualMap={SECTION_VISUAL}
        iconMap={SECTION_ICON}
        collapsible
      />
    ) : null;
  const pinsCard =
    col2Pins.length > 0 ? (
      <ColumnSections<EmployeeRow, SectionKey>
        sections={col2Pins}
        data={employee}
        t={t}
        visualMap={SECTION_VISUAL}
        iconMap={SECTION_ICON}
        collapsible
      />
    ) : null;

  return (
    <Box
      sx={{
        height: `calc(100vh - ${theme.toolbar.height}px)`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: PAGE_BG,
      }}
    >
      {/* Fixed sub-menu: identity header + tabs. Outside the scroll area, so it
          stays put with no sticky offset and no see-through. */}
      <Box sx={{ flexShrink: 0 }}>
        <SubHeader employee={employee} onBack={() => navigate(-1)} />
        {visibleIndexes.length > 0 && (
          <NavTabs tab={activeTab} onChange={setSelectedTab} visibleIndexes={visibleIndexes} />
        )}
      </Box>

      {/* Scrollable content area. Paiement keeps its own inner scroll (section
          sub-nav pinned + table filling); other tabs scroll as a block. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          ...(TABS[activeTab] === 'paiement'
            ? { display: 'flex', flexDirection: 'column', overflow: 'hidden' }
            : { overflowY: 'auto' }),
        }}
      >
        {/* ── Identity tab ─────────────────────────────────────────────────── */}
        {activeTab === 0 && (
          <Container
            maxWidth="xl"
            sx={{
              px: theme.detailPage.containerPx,
              pt: theme.detailPage.containerPt,
              pb: theme.detailPage.containerPb,
            }}
          >
            {/* Fixed 2-column grid. */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: theme.detailPage.gridCols,
                gap: theme.detailPage.gap,
                alignItems: 'stretch',
                mb: theme.detailPage.gap,
              }}
            >
              {/* Column 1 */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.detailPage.gap,
                  minWidth: 0,
                }}
              >
                {col1Cards}
                {documentsCard}
              </Box>

              {/* Column 2 */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.detailPage.gap,
                  minWidth: 0,
                }}
              >
                {addressCard}
                {(contactCard || emergencyCard) && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: theme.detailPage.gap,
                      alignItems: 'start',
                    }}
                  >
                    {contactCard}
                    {emergencyCard}
                  </Box>
                )}
                {paymentCard}
                {pinsCard}
                {deathCard}
                {commentCard}
              </Box>
            </Box>

            {/* Dependents — full width, TableTemplate provides the Paper card */}
            {classified2Access.canRead && (
              <DependentsTable
                dependents={employee.dependents ?? []}
                t={t}
                collapsible
                defaultExpanded={false}
                toolbar={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: theme.avatar.md,
                        height: theme.avatar.md,
                        bgcolor: SECTION_VISUAL.dependents.iconBg,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {SECTION_ICON.dependents(SECTION_VISUAL.dependents.iconColor)}
                    </Box>
                    <Typography
                      component="h2"
                      variant="subtitle1"
                      sx={{ color: theme.palette.text.body }}
                    >
                      {t('employeeDetail.sections.dependents')}
                    </Typography>
                  </Box>
                }
              />
            )}
          </Container>
        )}

        {/* ── Contract tab ─────────────────────────────────────────────────── */}
        {activeTab === 1 && id && (
          <EmployeeContracts employeeId={id} isActivated={employee.is_active ?? false} />
        )}

        {/* ── Payment tab ──────────────────────────────────────────────────── */}
        {TABS[activeTab] === 'paiement' && id && <PaymentSections employeeId={id} />}

        {/* ── Placeholder tabs ─────────────────────────────────────────────── */}
        {activeTabAllowed && activeTab > 1 && TABS[activeTab] !== 'paiement' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
            <Typography variant="body1" color="text.secondary">
              {t(`employeeDetail.tabs.${TABS[activeTab]}`)}
            </Typography>
          </Box>
        )}

        {/* Record granted but no `employee_record.*` tab → "aucun onglet" (a missing record is
            handled by the early return above, no /403 redirect). */}
        {!activeTabAllowed && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
            <Typography variant="body1" color="text.secondary">
              {t('employeeDetail.noAccessibleTab')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
